/**
 * Represents a container that holds various components and configurations.
 */
import RepositoryEntry from './RepositoryEntry';
import QuestionPart from './QuestionPart';

class Container extends RepositoryEntry {
    /**
     * Creates an instance of Container.
     */
    constructor(entry, $injector) {
        super(entry, $injector);
        // An Interaction Container is response-bearing: BaseObjectDirective's
        // save calls fireModelChange() on it, which needs $signals exactly like
        // ContentObject has (without it every Interaction Container save threw
        // after writing, was logged as autosave.failed and skipped the
        // MODELCHANGE broadcast).
        this.$signals = $injector.get('$signals');
        
        /**
         * A map of readable properties and their expected types.
         * @type {Object}
         */
        this.readables({
            'status': Number,
            'contentConfigurations': Array,
            'showTabs': Boolean,
            'showTitle': Boolean,
            'tabs': Array,
            'showCarousels': Boolean,
            'carousels': Array,
            'showIaaContainer': Boolean,
            'iaaContainer': Array,
            // Interaction Container: a structural container that carries a full
            // Assessura QuestionSheetItem on `interactionItem` instead of holding
            // child content objects. isShowInteraction() gates the candidate
            // render branch; getInteractionItem() reads the item.
            'showInteraction': Boolean,
            'interactionItem': Object,
            'objects': Array,
            'preferences': Object,
            'type': String
        });

        /**
         * The candidate's response to an Interaction Container is stored on the
         * container's own `attachement` (a SerializedCandidateState), exactly like
         * a content object's response, so the standard BaseObjectDirective save
         * pipeline persists it to ResponseData keyed by the container UUID (which
         * the server-side scorer reads). Only content objects declared this before.
         */
        this.accessables({
            'attachement': Object
        });

        /**
         * A map of references to external objects.
         * @type {Object}
         */
        this.references({
            'questionPart': QuestionPart
        });

        /**
         * A map of validators used to validate the container.
         * @type {Object}
         */
        this.validators({
            'ContainerEmptyValidator': 'ContainerEmptyValidator',
            'QPResponsePDFValidator': 'QPResponsePDFValidator'
        });

        /**
         * An array to store objects within the container.
         * @type {Array}
        this.objects = [];
         */
    }

    /**
     * Checks if the container is empty.
     * @returns {boolean} - True if the container has no objects, false otherwise.
     */
    isContainerEmpty() {
        return this.objects.length === 0;
    }

    /**
     * Retrieves the objects stored in the container.
     * @returns {Array} - The array of objects.
     */
    getObjects() {
        return this.objects;
    }

    /**
     * An Interaction Container is a response-bearing object, so it reports
     * objectType "interaction" for BaseObjectDirective's response machinery
     * (SHEET_WAIT_REQUEST, status). Other container types are not response
     * objects and have none.
     * @returns {string|undefined}
     */
    getObjectType() {
        return this.isShowInteraction && this.isShowInteraction() ? 'interaction' : undefined;
    }
}

export default Container;